import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  TeButton,
  TeCallout,
  TeContainer,
  TeContainerSlim,
  TeDivider,
  TeIcon,
  TeText,
} from '@topcon/tuicl-react';
import { PlaceholderContainer, useNotification } from '@topcon/topcon-utils';
import { Statuses } from '@topcon/tuicl/dist/common/component-types';

import { Section } from '../../utils/styling';
import Header from '../../components/Header';
import SelectableItem from '../../components/SelectableItem';
import {
  useCurrentBusinessPartner,
  useCurrentBusinessPartnerChangePM,
} from './businessPartner.queries';
import { useUpdateCurrentBusinessPartner } from './businessPartner.mutations';
import {
  BusinessPartnerAccessabilityEnum,
  BusinessPartnerPatchBody,
} from '../../api/generated/pap';

const Access = () => {
  const { t } = useTranslation('settings');
  const notificationService = useNotification();
  const [isPrivate, setIsPrivate] = useState(false);
  const { data, isLoading } = useCurrentBusinessPartner();

  const { data: currentBusinessPartnerChange, isPending: isBpChangePending } =
    useCurrentBusinessPartnerChangePM();
  const isBpPendingChange = currentBusinessPartnerChange != null;

  const { mutate: updateCurrentBusinessPartner, isPending } = useUpdateCurrentBusinessPartner({
    onError: () => {
      notificationService.addNotification(
        t('changesFailed'),
        t('changesFailedDesc'),
        Statuses.error
      );
    },
  });

  const defaultPrivate = data?.accessability === BusinessPartnerAccessabilityEnum.INVITE_ONLY;
  const loading = isLoading || isPending || isBpChangePending;

  const onUpdate = () => {
    const payload: BusinessPartnerPatchBody = {
      accessability: isPrivate
        ? BusinessPartnerAccessabilityEnum.INVITE_ONLY
        : BusinessPartnerAccessabilityEnum.REQUEST_TO_JOIN,
    };
    updateCurrentBusinessPartner(payload);
  };

  useEffect(() => {
    if (!data) return;
    setIsPrivate(data.accessability === BusinessPartnerAccessabilityEnum.INVITE_ONLY);
  }, [data]);

  const renderLoader = () => {
    return (
      <Section $padding="16px">
        <PlaceholderContainer
          borderRadius={4}
          grow
          backgroundColor="background.secondary"
          hasLoader
          text={t('loading')}
        />
      </Section>
    );
  };

  return (
    <TeContainer column grow gap="xs" padding="xs" overflow="auto" alignItems="center">
      <Header text={t('access.title')} />

      {loading ? (
        renderLoader()
      ) : (
        <Section>
          <TeContainer column fill>
            {isBpPendingChange ? (
              <TeCallout
                status="warning"
                icon="info"
                text={t('generalInformation.pendingChangesCallout')}
                grow
              />
            ) : null}
            <TeContainerSlim fill gap="s">
              <SelectableItem
                icon="lock"
                onClick={() => {
                  setIsPrivate(true);
                }}
                active={isPrivate}
                title={t('access.privateInviteOnly.title')}
                disabled={isBpPendingChange}
              >
                <TeContainerSlim gap="xs" alignItems="center">
                  <TeIcon name="close" />
                  <TeText size={12}>{t('access.privateInviteOnly.onlyInvite')}</TeText>
                </TeContainerSlim>
                <TeContainerSlim gap="xs" alignItems="center">
                  <TeIcon name="close" />
                  <TeText size={12}>{t('access.privateInviteOnly.hideInSearch')}</TeText>
                </TeContainerSlim>
              </SelectableItem>
              <SelectableItem
                icon="lock_open"
                onClick={() => {
                  setIsPrivate(false);
                }}
                active={!isPrivate}
                title={t('access.publicRequestToJoin.title')}
                disabled={isBpPendingChange}
              >
                <TeContainerSlim gap="xs" alignItems="center">
                  <TeIcon name="check" />
                  <TeText size={12}>{t('access.publicRequestToJoin.requestToJoin')}</TeText>
                </TeContainerSlim>
                <TeContainerSlim gap="xs" alignItems="center">
                  <TeIcon name="check" />
                  <TeText size={12}>{t('access.publicRequestToJoin.showInSearch')}</TeText>
                </TeContainerSlim>
              </SelectableItem>
            </TeContainerSlim>
          </TeContainer>

          <TeDivider dividerColor="border.secondary" />

          <TeContainer fill justifyContent="flex-end">
            <TeButton
              text={t('saveChanges')}
              size="s"
              disabled={defaultPrivate === isPrivate}
              teclick={onUpdate}
            />
          </TeContainer>
        </Section>
      )}
    </TeContainer>
  );
};

export default Access;
